import { addCalendarDays, cachedFormatter, calendarDayInZone, instantFromWallClock, wallClockInZone } from '../zonedTime';
import { fill, t } from '../strings';
import { visitDayWords, visitWindowWords } from './jobMessages';
import type { ScheduleCalendarEvent } from '../work/scheduleCalendar';
import { SCHEDULE_FIRST_HOUR, SCHEDULE_LAST_HOUR, type ScheduleBlock } from './scheduleView';

/**
 * The dispatch board and the per-worker requirement gate (piece 29): who can
 * get there fastest and is qualified. Pure: the service reads the calendar,
 * the positions, the site's requirements and the travel cache, and this says
 * what they add up to.
 */

export interface RequirementInForce {
  requirementTypeId: string;
  name: string;
}

export interface CompletionHeld {
  requirementTypeId: string;
  status: string;
  /** ISO date or instant; null where the completion never expires. */
  expiresAt: string | null;
}

export interface WorkerGate {
  ok: boolean;
  /** Each requirement the worker does not hold in force on the day, with the date it lapsed where it did. */
  missing: { requirementTypeId: string; name: string; expiredOn: string | null }[];
}

/**
 * 🔨 The gate acts per worker, never per line: a worker whose named requirement
 * has expired is Blocked with it named, and the worker beside them is not. A
 * completion counts when it is Current and unexpired on the day of the visit.
 */
export function workerGate(requirements: readonly RequirementInForce[], completions: readonly CompletionHeld[], on: string): WorkerGate {
  const missing: WorkerGate['missing'] = [];
  for (const requirement of requirements) {
    const rows = completions.filter((c) => c.requirementTypeId === requirement.requirementTypeId);
    const inForce = rows.some((c) => c.status === 'Current' && (c.expiresAt === null || c.expiresAt.slice(0, 10) >= on));
    if (inForce) continue;
    const lapsed = rows.map((c) => c.expiresAt?.slice(0, 10) ?? null).filter((d): d is string => d !== null).sort().pop() ?? null;
    missing.push({ requirementTypeId: requirement.requirementTypeId, name: requirement.name, expiredOn: lapsed });
  }
  return { ok: missing.length === 0, missing };
}

/** The sentence a Blocked worker carries, naming the requirement. */
export function gateReason(missing: WorkerGate['missing']): string | null {
  if (missing.length === 0) return null;
  return missing.map((m) => (m.expiredOn ? `${m.name} expired ${m.expiredOn}` : `${m.name} not held`)).join('; ');
}

/** Whether two windows share any instant; an open end is a point in time. */
export function windowsMeet(aStart: Date, aEnd: Date | null, bStart: Date, bEnd: Date | null): boolean {
  const aStop = (aEnd ?? aStart).getTime();
  const bStop = (bEnd ?? bStart).getTime();
  return aStart.getTime() <= bStop && bStart.getTime() <= aStop;
}

/** A candidate below the line's own required level is Blocked with the level named. */
export function levelBlock(held: { level: number; name: string } | null, required: { level: number; name: string }): string | null {
  if (!held) return `holds no skill level; the line needs ${required.name}`;
  if (held.level < required.level) return `${held.name} is below the ${required.name} this line needs`;
  return null;
}

/** 🔒 Decision 3101: the other job's visit a clash is, which Ask to Move names; Fixed where it carries a reason (decision 3100). */
export interface BoardClashBooking {
  visitId: string;
  workOrderId: string;
  workOrderNumber: string;
  visitLabel: string;
  fixedReason: string | null;
}

/** 🔒 The event that makes a candidate busy in the job's window (decision 3070), named on their row and in Assign's refusal. */
export interface BoardClash {
  subject: string;
  startsAt: string;
  /** Null where the event names no end. */
  endsAt: string | null;
  isAllDay: boolean;
  /** 🔒 Decision 3101: the other job's visit where the clash is one, else null; absent from an older read. */
  booking?: BoardClashBooking | null;
}

/** 🔒 Decision 3101: Ask to Move stands on a clash that is another job's visit that Can move, never on a Fixed one, a meeting or leave. */
export function asksToMove(clash: BoardClash | null): clash is BoardClash & { booking: BoardClashBooking } {
  return clash !== null && clash.booking !== undefined && clash.booking !== null && (clash.booking.fixedReason === null || clash.booking.fixedReason.trim() === '');
}

/** 🔒 Decision 3101: the feed entry's type Ask to Move writes, as the While You Were Gone tile groups it. */
export const ASK_TO_MOVE_ENTRY_TYPE = 'workOrderVisit.askedToMove';

/** Who an ask reached: the other job's Deal owner or its salesperson, or the manager told in their place. */
export type AskToMoveRole = 'DealOwner' | 'Salesperson';

/**
 * 🔒 Decision 3101: what Ask to Move answers, the people told and how many texts went. `texted` is true only where this ask
 * sent a new text the provider took; a repeat of an earlier ask, a send that failed and a person with no mobile read false (T2256).
 */
export interface AskToMoveAnswer {
  workOrderNumber: string;
  visitLabel: string;
  told: { personId: string; name: string; role: AskToMoveRole; inPlaceOf: string | null; texted: boolean }[];
  texts: number;
}

export interface AskToMoveFacts {
  needingJob: string;
  needingSite: string;
  workerName: string;
  windowStart: string;
  windowEnd: string;
  zone: string;
  otherJob: string;
  otherVisit: string;
  otherSite: string;
  /** The person a manager is told in place of, where they are on PTO. */
  inPlaceOf: string | null;
}

/**
 * 🔒 Ask to Move's words (decision 3101): the feed's sentence and the text,
 * each naming the job that needs the worker, the worker and the window in the
 * Organization's zone, and the booking asked about. It asks; it moves nothing.
 */
export function askToMoveWords(facts: AskToMoveFacts): { summary: string; text: string } {
  const when = `${visitDayWords(facts.windowStart, facts.zone)}, ${visitWindowWords(facts.windowStart, facts.windowEnd, facts.zone)}`;
  const needing = `${facts.needingJob}${facts.needingSite ? ` at ${facts.needingSite}` : ''}`;
  const other = `${facts.otherJob} ${facts.otherVisit}${facts.otherSite ? ` at ${facts.otherSite}` : ''}`;
  const standIn = facts.inPlaceOf ? ` You are asked in place of ${facts.inPlaceOf}, who is off today.` : '';
  const summary = `${needing} needs ${facts.workerName} ${when}. Can ${other} move?${standIn}`;
  return { summary, text: `Ask to Move: ${summary} Nothing has moved yet.` };
}

/** 🔒 Decision 3101: one ask's text key, so the same ask for the same window sends each person one text. */
export function askToMoveRevision(visitId: string, needingWorkOrderId: string, personId: string, windowStart: string): string {
  return `${visitId}.askToMove.${needingWorkOrderId}.${personId}.${windowStart}`;
}

/** 🔒 Decision 3101: how many days Propose a New Time searches, the owner's seven. */
export const PROPOSAL_DAYS = 7;

/**
 * 🔒 The starts Propose a New Time tries (decision 3101), earliest first: the
 * job's own start where it has not passed, then each hour on the hour inside
 * 06:00 to 20:00 in the Organization's zone, for seven days from the first.
 * Each window keeps the job's length and ends by 20:00 on its own day.
 */
export function proposalSlots(from: Date, lengthMs: number, zone: string, options: { exactFirst: boolean; days?: number }): { start: Date; end: Date }[] {
  const out: { start: Date; end: Date }[] = [];
  const firstDay = calendarDayInZone(from, zone);
  const hour = (h: number) => String(h).padStart(2, '0');
  for (let offset = 0; offset < (options.days ?? PROPOSAL_DAYS); offset += 1) {
    const day = addCalendarDays(firstDay, offset);
    const opens = instantFromWallClock(`${day}T${hour(SCHEDULE_FIRST_HOUR)}:00`, zone).getTime();
    const closes = instantFromWallClock(`${day}T${hour(SCHEDULE_LAST_HOUR)}:00`, zone).getTime();
    const starts = new Set<number>();
    if (offset === 0 && options.exactFirst && from.getTime() >= opens) starts.add(from.getTime());
    for (let h = SCHEDULE_FIRST_HOUR; h < SCHEDULE_LAST_HOUR; h += 1) {
      const at = instantFromWallClock(`${day}T${hour(h)}:00`, zone).getTime();
      if (at > from.getTime() || (at === from.getTime() && !options.exactFirst)) starts.add(at);
    }
    for (const start of [...starts].sort((a, b) => a - b)) {
      if (start + lengthMs <= closes) out.push({ start: new Date(start), end: new Date(start + lengthMs) });
    }
  }
  return out;
}

/** 🔒 Decision 3101: the visit Propose a New Time opens the job's Reschedule on, the way Edit Visit reads it. */
export interface BoardProposalVisit {
  id: string;
  version: number;
  label: string;
  status: ScheduleBlock['status'];
  windowStart: string;
  windowEnd: string | null;
  crew: { employmentId: string; personId: string; name: string; isLead: boolean }[];
  carriesBooking: boolean;
  fixedReason: string | null;
}

/** 🔒 Decision 3101: the earliest free window inside seven days and the qualified worker free in it, or none. */
export interface BoardProposal {
  zone: string;
  companyId: string;
  /** The first start tried and the last end, which the board names where nobody is free. */
  searchedFrom: string;
  searchedTo: string;
  found: { startsAt: string; endsAt: string; employmentId: string; personId: string; name: string } | null;
  /** The job's first visit still to come, which the Reschedule moves; null where the job has none. */
  visit: BoardProposalVisit | null;
}

/** A booked visit on a candidate's strip: what the Schedule view's block names, read-only on the board. */
export type BoardStripVisit = Pick<ScheduleBlock, 'visitId' | 'workOrderId' | 'workOrderNumber' | 'visitLabel' | 'siteName' | 'windowStart' | 'windowEnd' | 'status' | 'fixedReason'>;

/** 🔒 A candidate's day as their strip draws it (decision 3070): the calendar events and the visits of the window's day. */
export interface BoardDay {
  events: ScheduleCalendarEvent[];
  visits: BoardStripVisit[];
}

export interface BoardCandidate {
  employmentId: string;
  personId: string;
  name: string;
  title: string | null;
  skillLevelName: string | null;
  /** Every reason the candidate may not go, each named; empty for a qualified candidate. */
  blocked: string[];
  /**
   * 🔒 Decision 3071: the candidate's one block is a level below the line's, which a typed reason gets past.
   * False for a qualified candidate, for one the site's gate blocks, and for one holding no level at all.
   */
  passable: boolean;
  /** Drive minutes from the candidate's dispatch origin to the job's Site; null where the cache holds no figure. */
  driveMinutes: number | null;
  /** 🔒 Decision 3070: the event holding the candidate in the window; null where they are free. */
  clash: BoardClash | null;
  /** 🔒 Decision 3070: the candidate's day, which their strip draws. */
  day: BoardDay;
  /** 🔒 Decision 5572: whether the candidate holds an active login; absent from an older reply, which reads as held. */
  hasLogin?: boolean;
  /**
   * 🔒 Decision 12850: the window falls outside the candidate's working pattern on its day, so they read as unavailable
   * and are warned, never refused. Only the answer travels, never the hours; absent from an older reply, which reads as inside.
   */
  outsidePattern?: boolean;
}

/**
 * 🔨 The sort is available, then qualified, then nearest. A busy candidate is
 * listed after every free one with the clash named (decision 3070), and so is one
 * outside their working pattern (decision 12850); a Blocked
 * candidate is listed with the reason, never hidden, and a candidate with no
 * drive figure sorts after every one with one.
 */
export function boardOrder(a: BoardCandidate, b: BoardCandidate): number {
  const unavailable = (c: BoardCandidate) => c.clash !== null || c.outsidePattern === true;
  const available = Number(unavailable(a)) - Number(unavailable(b));
  if (available !== 0) return available;
  const qualified = Number(a.blocked.length > 0) - Number(b.blocked.length > 0);
  if (qualified !== 0) return qualified;
  const near = (a.driveMinutes ?? Number.POSITIVE_INFINITY) - (b.driveMinutes ?? Number.POSITIVE_INFINITY);
  if (near !== 0 && Number.isFinite(near)) return near;
  if (a.driveMinutes === null && b.driveMinutes !== null) return 1;
  if (b.driveMinutes === null && a.driveMinutes !== null) return -1;
  return a.name.localeCompare(b.name);
}

/**
 * 🔒 The hours a candidate's strip draws (decision 3070): 06:00 to 20:00 in the
 * Organization's zone, widened to hold a window reaching outside them, or at
 * phone width the window and two hours each side. The day is the one the
 * window starts on; a window running past it is drawn to midnight.
 */
export function stripHours(windowStart: string, windowEnd: string, day: string, zone: string, narrow: boolean): { firstHour: number; lastHour: number } {
  const hourOf = (instant: string): number => {
    const wall = wallClockInZone(instant, zone);
    const onDay = wall.slice(0, 10);
    if (onDay < day) return 0;
    if (onDay > day) return 24;
    const [h, m] = wall.slice(11, 16).split(':').map(Number) as [number, number];
    return h + m / 60;
  };
  const from = hourOf(windowStart);
  const to = Math.max(hourOf(windowEnd), from);
  if (narrow) return { firstHour: Math.max(0, Math.floor(from) - 2), lastHour: Math.min(24, Math.max(Math.ceil(to), Math.floor(from) + 1) + 2) };
  return { firstHour: Math.min(SCHEDULE_FIRST_HOUR, Math.floor(from)), lastHour: Math.max(SCHEDULE_LAST_HOUR, Math.min(24, Math.ceil(to))) };
}

/** 🔒 Decision 3072: the tier whose notice window marks an unbooked job Starts Soon; its hours are the tier row's own. */
export const STARTS_SOON_TIER_KEY = 'high';

/**
 * 🔒 Decision 3072 (T2183): an unbooked job whose start falls inside the High
 * tier's notice window, its `maxNoticeHours` read from the row in force and
 * never a second constant. A start already past reads Starts Soon too, since
 * nobody is booked for it yet; a job with no start does not.
 */
export function startsSoon(scheduledStart: string | null, booked: boolean, now: Date, maxNoticeHours: number | null): boolean {
  if (booked || scheduledStart === null || maxNoticeHours === null) return false;
  return Date.parse(scheduledStart) - now.getTime() <= maxNoticeHours * 3_600_000;
}

/**
 * 🔒 Decision 4340: the two groups Jobs to Dispatch draws, Needs a Crew first.
 * A job is Sent where a visit of it is Dispatched or begun, or the job itself
 * is In Progress; every other open job in the list needs a crew.
 */
export const BOARD_JOB_GROUPS = ['NeedsCrew', 'Sent'] as const;
export type BoardJobGroup = (typeof BOARD_JOB_GROUPS)[number];

/**
 * What a card waits on: no visit booked yet, a visit with nobody on it, a crew booked before decision 4340 and never
 * sent, or the crew it was sent to. 🔒 Decision 5570: the Unscheduled list says which of the first two a job waits on.
 */
export interface BoardWaits {
  kind: 'NoVisit' | 'NoCrew' | 'NotSent' | 'Sent';
  /** The crew by name, the lead first; empty where nobody is on the visit. */
  names: string[];
  /** The visit's start, which the line names; null where no visit stands. */
  windowStart: string | null;
}

/** The facts the group and the waits-on line read: the job's status and its visits still to come or begun, in booking order. */
export interface BoardJobFacts {
  status: string;
  visits: readonly { status: string; windowStart: string; crew: readonly string[] }[];
}

/** 🔒 Decision 4340: which group a job stands in, and what its card says it waits on. */
export function boardJobGroup(facts: BoardJobFacts): { group: BoardJobGroup; waits: BoardWaits } {
  const sent = facts.visits.find((visit) => visit.status === 'Dispatched' || visit.status === 'InProgress');
  if (sent) return { group: 'Sent', waits: { kind: 'Sent', names: [...sent.crew], windowStart: sent.windowStart } };
  if (facts.status === 'InProgress') return { group: 'Sent', waits: { kind: 'Sent', names: [], windowStart: null } };
  // A visit booked with a crew before booking sent it stands here until a write of its crew sends it.
  const unsent = facts.visits.find((visit) => visit.status === 'Scheduled' && visit.crew.length > 0);
  if (unsent) return { group: 'NeedsCrew', waits: { kind: 'NotSent', names: [...unsent.crew], windowStart: unsent.windowStart } };
  // 🔒 Decision 5570: a job with no visit still to come waits on a visit; one whose visit names nobody waits on a crew.
  if (facts.visits.length === 0) return { group: 'NeedsCrew', waits: { kind: 'NoVisit', names: [], windowStart: null } };
  return { group: 'NeedsCrew', waits: { kind: 'NoCrew', names: [], windowStart: facts.visits[0]?.windowStart ?? null } };
}

/** 🔒 Decision 4340: the card's one line, *No crew yet* or *Sent to Lee Harmon · Wed 7:30 AM*, the day and hour in the zone. */
export function waitsOnWords(waits: BoardWaits, zone: string): string {
  if (waits.kind === 'NoVisit') return t('work.dispatch.waitsNoVisit');
  const when = waits.windowStart ? cachedFormatter(zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(waits.windowStart)) : '';
  // 🔒 Decision 5570: a visit booked with nobody on it names its day and hour, so the row says it waits on a crew and not on a visit.
  if (waits.kind === 'NoCrew') return when ? `${t('work.dispatch.waitsNoCrew')} · ${when}` : t('work.dispatch.waitsNoCrew');
  const names = waits.names.join(', ');
  const words = fill(t(waits.kind === 'Sent' ? 'work.dispatch.waitsSent' : 'work.dispatch.waitsNotSent'), { names: names || t('work.dispatch.waitsNobody') });
  return when ? `${words} · ${when}` : words;
}

/** 🔒 Decision 4340: what the board's Assign and drop send. A drop names the hour on the strip; Assign names none and the server picks it. */
export interface BoardAssignInput {
  employmentId: string;
  lineId?: string | null;
  /** 🔒 Decision 3071: why a worker below the line's level goes anyway. */
  overrideReason?: string | null;
  /** The drop's hour as a wall clock, `YYYY-MM-DDTHH:mm`, in `timeZone`. */
  startsAt?: string | null;
  timeZone?: string | null;
}

/** 🔒 Decision 4340: the visit Assign or the drop booked or added the worker to, and whether it took a later hour than the job's own. */
export interface BoardAssignAnswer {
  visitId: string;
  visitLabel: string;
  status: string;
  windowStart: string;
  windowEnd: string | null;
  /** The worker joined a visit still to come rather than a new one being booked. */
  added: boolean;
  /** The job's own window was held on the worker's day, so the next free hour was booked. */
  later: boolean;
  zone: string;
}

/**
 * 🔒 Who Can Go inside a booking form (decision 5570): the board's own ranking for a site and a window, read before a
 * job exists or for a visit still to book, free in the window first, then qualified, then nearest, as `boardOrder` sorts.
 */
export interface WhoCanGoAnswer {
  zone: string;
  day: string;
  windowStart: string;
  windowEnd: string;
  candidates: BoardCandidate[];
}

/** 🔒 Decision 4340: the notice after Assign or a drop, naming the visit and when it starts, in the words the card uses. */
export function assignedWords(answer: BoardAssignAnswer): string {
  const when = cachedFormatter(answer.zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(answer.windowStart));
  const key = answer.added ? 'work.dispatch.assigned' : answer.later ? 'work.dispatch.assignedLater' : 'work.dispatch.assignedBooked';
  return fill(t(key), { visit: answer.visitLabel, when });
}
